import type { Frequency, Rhythm, TimeOfDay } from "@/lib/calendar";

/**
 * Google's screen opens in the same tab, so whatever the person was doing has to survive
 * the round trip: which step they were scheduling, where to land afterwards, and — when a
 * reconnect interrupted an add — the exact choice they'd made, so it's waiting for them.
 */

const KEY = "unfold_calendar_handoff";

/** A Find a time choice, kept so a reconnect brings the person straight back to it. */
export interface PlanDraft {
  frequency: Frequency;
  durationMin: number;
  prefer: TimeOfDay;
  /** The day picked on the month, `yyyy-MM-dd`. */
  day: string;
  slot: { day: string; start: number; end: number } | null;
  rhythm: Rhythm | null;
}

export interface CalendarHandoff {
  /** Where the connect started; decides what follows "connected". */
  origin: "momentum" | "settings" | "plan";
  journalId?: string;
  actionId?: string;
  stepText?: string;
  /** Path (with query) to return to after Google. */
  returnPath: string;
  draft?: PlanDraft;
}

export function saveHandoff(handoff: CalendarHandoff) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(handoff));
  } catch {
    // Private mode: the person lands back without their place, which is recoverable.
  }
}

export function readHandoff(): CalendarHandoff | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as CalendarHandoff) : null;
  } catch {
    return null;
  }
}

export function clearHandoff() {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing to clear.
  }
}

export type CalendarResult = "connected" | "missing_events" | "cancelled";

/** The `?calendar=` result Google's round trip lands with, if any. */
export function readCalendarResult(): CalendarResult | null {
  const v = new URLSearchParams(window.location.search).get("calendar");
  return v === "connected" || v === "missing_events" || v === "cancelled" ? v : null;
}

/** Drop `?calendar=` so a refresh doesn't replay the result. */
export function clearCalendarResult() {
  const url = new URL(window.location.href);
  if (!url.searchParams.has("calendar")) return;
  url.searchParams.delete("calendar");
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
}

/** Absolute URL for the backend to send the person back to. */
export function returnUrl(path: string): string {
  const base = import.meta.env.BASE_URL?.replace(/\/+$/, "") || "";
  return `${window.location.origin}${base}${path}`;
}
